import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { authService } from '../../../services/auth.service';
import { useHaptic } from '../../../hooks/useHaptic';
import { toast } from 'sonner';
import posthog from '../../../posthog';
import image1 from '../../../assets/image1.jpg';

export const AuthPage = () => {
  const navigate = useNavigate();
  const { success: hapticSuccess, error: hapticError } = useHaptic();
  
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'initial' | 'verify'>('initial');
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      if (!credentialResponse.credential) {
        throw new Error("No credential received from Google");
      }
      
      const data = await authService.googleLogin(credentialResponse.credential);
      localStorage.setItem('name', data.user || 'User');
      localStorage.setItem('role', data.role);
      
      localStorage.setItem('token', 'authenticated');
      if (data.uid) posthog.identify(data.uid, { role: data.role });
      posthog.capture('login_completed', { authentication_method: 'google' });

      hapticSuccess();
      toast.success(`Welcome back, ${data.user || 'User'}!`);
      navigate('/');
    } catch (err: any) {
      hapticError();
      const errorMsg = err.message || 'Failed to authenticate with Google';
      toast.error(errorMsg);
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setIsLoading(true);
    try {
      await authService.requestOtp(email);
      setStep('verify');
      hapticSuccess();
      toast.success("OTP sent to your email!");
    } catch (err: any) {
      hapticError();
      toast.error(err.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }
    setIsLoading(true);
    try {
      const data = await authService.verifyOtp(email, otp);
      localStorage.setItem('name', data.user || 'User');
      localStorage.setItem('role', data.role);
      localStorage.setItem('token', 'authenticated');
      if (data.uid) posthog.identify(data.uid, { role: data.role });
      posthog.capture('login_completed', { authentication_method: 'otp' });
      hapticSuccess();
      toast.success(`Welcome back, ${data.user || 'User'}!`);
      navigate('/');
    } catch (err: any) {
      hapticError();
      toast.error(err.message || "Invalid OTP");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grain bg-login-mesh flex items-center justify-center p-4 md:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="p-2 bg-white/40 backdrop-blur-sm rounded-[2.5rem] shadow-2xl shadow-orange-900/10 w-full max-w-5xl"
      >
        <div className="w-full bg-white rounded-[2rem] border border-white/80 shadow-xl overflow-hidden flex flex-col md:flex-row min-h-[600px] h-[85vh] max-h-[800px]">

          {/* ── Left Panel: Brand ── */}
          <div className="w-full md:w-[50%] p-8 md:p-12 flex flex-col justify-between relative overflow-hidden min-h-[240px] text-left"
            style={{
              background: `url(${image1}) center/cover no-repeat` }}
          >
            <div className="flex items-center gap-2.5 z-10 relative">
              <img src="/favicon.png" alt="LinkAlum" className="w-8 h-8" />
              <span className="font-outfit font-bold text-xl text-slate-900 tracking-tight">LinkAlum</span>
            </div>
            <div className="mt-auto z-10 relative text-left">
              <span className="text-[10px] font-bold text-orange-800/60 uppercase tracking-widest block mb-3 font-outfit">
                Fundraising Intelligence
              </span>
              <h1 className="font-outfit font-extrabold text-2xl md:text-[1.75rem] text-slate-900 leading-tight tracking-tight">
                Your alumni fundraising command centre.
              </h1>
              <p className="text-slate-500 text-xs font-medium mt-3 leading-relaxed max-w-[260px]">
                Donor intelligence, proposal matching, and campaign tracking — all in one place.
              </p>
            </div>
            <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full bg-orange-200/30 blur-3xl pointer-events-none" />
            <div className="absolute -left-10 -bottom-10 w-44 h-44 rounded-full bg-accent/15 blur-3xl pointer-events-none" />
          </div>

          {/* ── Right Panel: Form ── */}
          <div className="w-full md:w-[55%] p-8 md:p-12 lg:p-16 flex flex-col justify-center bg-white relative overflow-y-auto items-center">
            <div className="w-full max-w-sm mx-auto text-center relative">
              
              <AnimatePresence mode="wait">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="w-full flex flex-col items-center"
                >
                  <h2 className="font-outfit font-bold text-2xl text-slate-900 tracking-tight mb-1.5">
                    Welcome back
                  </h2>
                  <p className="text-xs text-slate-400 leading-relaxed mb-7 font-medium">
                    Sign in with your ACR Google Account
                  </p>
                  <div className="w-full">
                    {step === 'initial' ? (
                      <form onSubmit={handleSendOtp} className="flex flex-col gap-3">
                        <input
                          type="email"
                          placeholder="Email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-4 py-3 rounded-md bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all"
                          required
                        />
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="w-full py-3 rounded-md bg-slate-900 text-white font-medium text-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          {isLoading ? "Sending..." : "Send Login Code"}
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyOtp} className="flex flex-col gap-3">
                        <p className="text-xs text-slate-500 text-left mb-1">
                          Code sent to <strong>{email}</strong>
                          <button type="button" onClick={() => setStep('initial')} className="ml-2 text-accent hover:underline">Change</button>
                        </p>
                        <input
                          type="text"
                          placeholder="6-digit code"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all tracking-widest font-mono"
                          required
                          maxLength={6}
                        />
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="w-full py-3 rounded-xl bg-accent text-white font-medium text-sm hover:bg-orange-600 transition-colors shadow-lg shadow-accent/20 disabled:opacity-50"
                        >
                          {isLoading ? "Verifying..." : "Verify and Login"}
                        </button>
                      </form>
                    )}
                  </div>

                  <div className="w-full flex items-center gap-3 my-4 opacity-50">
                    <div className="h-px bg-slate-300 flex-1"></div>
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-widest">or</span>
                    <div className="h-px bg-slate-300 flex-1"></div>
                  </div>


                  <div className="w-full flex justify-center py-4">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => {
                        hapticError();
                        toast.error("Google Login Failed");
                      }}
                      useOneTap
                      theme="filled_black"
                      shape="pill"
                      size="large"
                    />
                  </div>

                  

                  

                </motion.div>
              </AnimatePresence>

            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
